import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { Calendar } from '../../../models/interfaces';
import { CalendarCrudService } from '../../../modules/calendar/services/calendar-crud.service';
import { CalendarChoice, orderCalendarChoices } from '../handlers/calendar-choice.util';

/**
 * Calendriers proposés à la personne quand une action de présence ne sait pas
 * lequel viser (MOKA-407). Partagé par `CalendarVoiceHandler` (phrase libre) et
 * `VoiceGuidedFlowService` (flow « Ajouter au calendrier »).
 *
 * « Accessibles » = les calendriers de l'organisation active chargés avec le
 * profil (`profile.organisation.calendars`, filtrés par la RLS) : exactement
 * ceux que le menu Calendrier propose.
 *
 * « Dernier utilisé » = le calendrier de la dernière présence que la personne
 * a posée (base de données). Le calendrier ouvert à l'écran n'est pas
 * mémorisé hors de la page (état en mémoire, vide ailleurs) et la page ne
 * garde rien dans `localStorage` : la dernière présence est la seule trace
 * durable, la même sur tous les appareils, et celle que « Qui est au bureau »
 * utilise déjà (MOKA-266). Poser une présence depuis Koffi met donc à jour le
 * « dernier utilisé » tout seul.
 */
@Injectable({ providedIn: 'root' })
export class VoiceCalendarChoiceService {
  private auth = inject(AuthService);
  private calendarCrud = inject(CalendarCrudService);

  /** Calendriers de l'organisation active, avec identifiant. */
  accessibleCalendars(): Calendar[] {
    return (this.auth.profile?.organisation?.calendars ?? []).filter(c => typeof c.id === 'number');
  }

  /** Identifiant du dernier calendrier utilisé s'il est encore accessible, sinon `null`. */
  async lastUsedCalendarId(): Promise<number | null> {
    const profileId = this.auth.profile?.id;
    if (!profileId) return null;
    try {
      const last = await firstValueFrom(this.calendarCrud.getLastPresenceForProfile(profileId));
      const id = last?.calendar_id ?? null;
      return id !== null && this.accessibleCalendars().some(c => c.id === id) ? id : null;
    } catch {
      // Sans dernière présence lisible, on propose simplement la liste.
      return null;
    }
  }

  /** Choix à afficher : dernier utilisé en premier. */
  async choices(): Promise<CalendarChoice[]> {
    return orderCalendarChoices(this.accessibleCalendars(), await this.lastUsedCalendarId());
  }
}
